import { useEffect, useState, type FormEvent } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import Field from '../../components/admin/Field';
import ImageUploader from '../../components/admin/ImageUploader';
import AudioUploader from '../../components/admin/AudioUploader';
import { ErrorState, Loading } from '../../components/StatusStates';
import { useAdminProfile, useUpdateProfile } from '../../hooks/use-admin';
import { ApiError } from '../../api/client';
import type { Profile, SkillGroup, SocialLink, TimelineItem } from '../../api/types';
import { tagsToText, textToTags } from '../../lib/format';

type ProfileForm = Omit<Profile, 'id' | 'updatedAt'>;

function emptySkillGroup(): SkillGroup {
  return { name: '', items: [] };
}

function emptyTimelineItem(): TimelineItem {
  return { period: '', title: '', organization: '', description: '' };
}

function emptySocialLink(): SocialLink {
  return { label: '', url: '' };
}

export default function ProfileEdit() {
  const profileQuery = useAdminProfile();
  const updateMutation = useUpdateProfile();

  const [form, setForm] = useState<ProfileForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (profileQuery.data) {
      const { id: _id, updatedAt: _updatedAt, ...rest } = profileQuery.data;
      setForm(rest);
    }
  }, [profileQuery.data]);

  const update = (patch: Partial<ProfileForm>) => {
    setSuccessMessage(null);
    setForm((current) => (current ? { ...current, ...patch } : current));
  };

  const validate = (): boolean => {
    if (!form) return false;
    if (!form.name.trim() || !form.title.trim() || !form.tagline.trim() || !form.bio.trim()) {
      setFormError('姓名、职业定位、简短自述与详细简介均为必填项。');
      return false;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setFormError('邮箱格式不正确。');
      return false;
    }
    if (form.skillGroups.some((group) => !group.name.trim())) {
      setFormError('每个技能分组都需要填写名称。');
      return false;
    }
    if (
      form.timeline.some(
        (item) => !item.period.trim() || !item.title.trim() || !item.organization.trim(),
      )
    ) {
      setFormError('每条经历都需要填写时间段、标题与机构名称。');
      return false;
    }
    if (form.socials.some((link) => !link.label.trim() || !link.url.trim())) {
      setFormError('每条社交链接都需要填写名称与地址，不需要的条目请删除。');
      return false;
    }
    setFormError(null);
    return true;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form || !validate()) return;
    try {
      await updateMutation.mutateAsync({
        ...form,
        name: form.name.trim(),
        title: form.title.trim(),
        tagline: form.tagline.trim(),
        email: form.email?.trim() || null,
        skillGroups: form.skillGroups.map((group) => ({
          name: group.name.trim(),
          items: group.items.map((item) => item.trim()).filter(Boolean),
        })),
        timeline: form.timeline.map((item) => ({
          period: item.period.trim(),
          title: item.title.trim(),
          organization: item.organization.trim(),
          description: item.description?.trim() || '',
        })),
        socials: form.socials.map((link) => ({
          label: link.label.trim(),
          url: link.url.trim(),
        })),
      });
      setSuccessMessage('个人资料已保存，公开页面将同步更新。');
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : '保存失败，请稍后重试');
    }
  };

  if (profileQuery.isLoading) return <Loading />;
  if (profileQuery.isError || !form) {
    return <ErrorState message="个人资料加载失败" onRetry={() => profileQuery.refetch()} />;
  }

  /* ---------------- 动态列表操作 ---------------- */
  const updateSkillGroup = (index: number, patch: Partial<SkillGroup>) => {
    update({
      skillGroups: form.skillGroups.map((group, i) =>
        i === index ? { ...group, ...patch } : group,
      ),
    });
  };

  const updateTimelineItem = (index: number, patch: Partial<TimelineItem>) => {
    update({
      timeline: form.timeline.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    });
  };

  const updateSocial = (index: number, patch: Partial<SocialLink>) => {
    update({
      socials: form.socials.map((link, i) => (i === index ? { ...link, ...patch } : link)),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="editor-grid">
        {formError && <div className="inline-alert inline-alert-error">{formError}</div>}
        {successMessage && (
          <div className="inline-alert inline-alert-success">{successMessage}</div>
        )}

        <section className="panel-form">
          <h2 className="admin-section-title">基础信息</h2>
          <div className="form-grid">
            <Field label="姓名" required>
              <input
                className="form-input"
                value={form.name}
                maxLength={100}
                onChange={(event) => update({ name: event.target.value })}
              />
            </Field>
            <Field label="职业定位" required hint="例如：软件工程师 · 技术写作者">
              <input
                className="form-input"
                value={form.title}
                maxLength={200}
                onChange={(event) => update({ title: event.target.value })}
              />
            </Field>
            <Field label="简短自述" required hint="显示在首页，不超过 500 字" className="form-field-full">
              <textarea
                className="form-textarea"
                rows={2}
                maxLength={500}
                value={form.tagline}
                onChange={(event) => update({ tagline: event.target.value })}
              />
            </Field>
            <Field label="联系邮箱">
              <input
                type="email"
                className="form-input"
                value={form.email ?? ''}
                maxLength={200}
                placeholder="mail@example.com"
                onChange={(event) => update({ email: event.target.value })}
              />
            </Field>
            <Field label="头像">
              <ImageUploader value={form.avatar} onChange={(avatar) => update({ avatar })} />
            </Field>
            <Field
              label="背景音乐"
              hint="开启后将在全站右上角显示 CD 控件，点击即可播放/暂停"
              className="form-field-full"
            >
              <div className="bgm-config">
                <label className="bgm-toggle">
                  <input
                    type="checkbox"
                    checked={form.bgmEnabled ?? false}
                    onChange={(event) => update({ bgmEnabled: event.target.checked })}
                  />
                  启用背景音乐
                </label>
                <AudioUploader
                  value={form.bgmUrl ?? null}
                  onChange={(bgmUrl) => update({ bgmUrl })}
                />
              </div>
            </Field>
            <Field
              label="详细简介"
              required
              hint="支持 Markdown 语法，将展示在「关于」页面"
              className="form-field-full"
            >
              <textarea
                className="form-textarea"
                rows={10}
                value={form.bio}
                onChange={(event) => update({ bio: event.target.value })}
              />
            </Field>
          </div>
        </section>

        <section className="panel-form">
          <div className="repeatable-header">
            <h2 className="admin-section-title" style={{ marginBottom: 0 }}>
              技能分组
            </h2>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => update({ skillGroups: [...form.skillGroups, emptySkillGroup()] })}
            >
              <Plus size={14} />
              添加分组
            </button>
          </div>
          <div className="repeatable-list">
            {form.skillGroups.map((group, index) => (
              <div key={index} className="repeatable-item">
                <div className="repeatable-item-row">
                  <Field label="分组名称">
                    <input
                      className="form-input"
                      value={group.name}
                      maxLength={50}
                      placeholder="例如：后端开发"
                      onChange={(event) => updateSkillGroup(index, { name: event.target.value })}
                    />
                  </Field>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() =>
                      update({ skillGroups: form.skillGroups.filter((_, i) => i !== index) })
                    }
                    aria-label={`删除技能分组 ${group.name || index + 1}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <Field label="技能项" hint="以逗号或顿号分隔">
                  <input
                    className="form-input"
                    value={tagsToText(group.items)}
                    placeholder="Node.js, TypeScript, MySQL"
                    onChange={(event) =>
                      updateSkillGroup(index, { items: textToTags(event.target.value) })
                    }
                  />
                </Field>
              </div>
            ))}
            {form.skillGroups.length === 0 && (
              <p className="form-hint">尚未添加技能分组，点击右上角按钮添加。</p>
            )}
          </div>
        </section>

        <section className="panel-form">
          <div className="repeatable-header">
            <h2 className="admin-section-title" style={{ marginBottom: 0 }}>
              经历时间线
            </h2>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => update({ timeline: [...form.timeline, emptyTimelineItem()] })}
            >
              <Plus size={14} />
              添加经历
            </button>
          </div>
          <div className="repeatable-list">
            {form.timeline.map((item, index) => (
              <div key={index} className="repeatable-item">
                <div className="repeatable-item-row">
                  <Field label="时间段">
                    <input
                      className="form-input"
                      value={item.period}
                      maxLength={50}
                      placeholder="2021 — 至今"
                      onChange={(event) =>
                        updateTimelineItem(index, { period: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="标题">
                    <input
                      className="form-input"
                      value={item.title}
                      maxLength={100}
                      placeholder="高级软件工程师"
                      onChange={(event) =>
                        updateTimelineItem(index, { title: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="机构名称">
                    <input
                      className="form-input"
                      value={item.organization}
                      maxLength={100}
                      placeholder="某科技公司"
                      onChange={(event) =>
                        updateTimelineItem(index, { organization: event.target.value })
                      }
                    />
                  </Field>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() =>
                      update({ timeline: form.timeline.filter((_, i) => i !== index) })
                    }
                    aria-label={`删除经历 ${index + 1}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <Field label="描述（可选）">
                  <input
                    className="form-input"
                    value={item.description ?? ''}
                    maxLength={500}
                    onChange={(event) =>
                      updateTimelineItem(index, { description: event.target.value })
                    }
                  />
                </Field>
              </div>
            ))}
            {form.timeline.length === 0 && (
              <p className="form-hint">尚未添加经历条目，点击右上角按钮添加。</p>
            )}
          </div>
        </section>

        <section className="panel-form">
          <div className="repeatable-header">
            <h2 className="admin-section-title" style={{ marginBottom: 0 }}>
              社交链接
            </h2>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => update({ socials: [...form.socials, emptySocialLink()] })}
            >
              <Plus size={14} />
              添加链接
            </button>
          </div>
          <div className="repeatable-list">
            {form.socials.map((link, index) => (
              <div key={index} className="repeatable-item">
                <div className="repeatable-item-row">
                  <Field label="名称">
                    <input
                      className="form-input"
                      value={link.label}
                      maxLength={50}
                      placeholder="GitHub"
                      onChange={(event) => updateSocial(index, { label: event.target.value })}
                    />
                  </Field>
                  <Field label="地址">
                    <input
                      className="form-input"
                      value={link.url}
                      maxLength={500}
                      placeholder="https://github.com/..."
                      onChange={(event) => updateSocial(index, { url: event.target.value })}
                    />
                  </Field>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() =>
                      update({ socials: form.socials.filter((_, i) => i !== index) })
                    }
                    aria-label={`删除链接 ${link.label || index + 1}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
            {form.socials.length === 0 && (
              <p className="form-hint">尚未添加社交链接，点击右上角按钮添加。</p>
            )}
          </div>
        </section>

        <div className="form-actions" style={{ marginTop: 0 }}>
          <button type="submit" className="btn btn-primary" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <span className="icon-inline">保存中…</span>
            ) : (
              <>
                <Save size={15} />
                保存全部修改
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
